import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { removeGenericFromType } from "@redotech/redo-flows-core-schema-common/type-system/data-types/generic-types";
import { schemas, SchemaType } from "./schemas/schemas";

/** Schema field the trigger must expose before this step can be inserted. */
export type AutomationStepRequiredSchemaField = "Email" | "Phone";

/** GraphQL kind, payload key, insertability, and required trigger-schema field. */
const TYPED_STEP_KIND_SPEC = {
  TRIGGER: {
    graphqlTypename: "MarketingAutomationTriggerStep",
    payloadKey: "trigger",
    insertable: false,
  },
  WAIT: {
    graphqlTypename: "MarketingAutomationWaitStep",
    payloadKey: "wait",
    insertable: true,
    requiresSchemaField: null,
  },
  WAIT_FOR_EVENT: {
    graphqlTypename: "MarketingAutomationWaitForEventStep",
    payloadKey: "waitForEvent",
    insertable: true,
    requiresSchemaField: null,
  },
  SEND_EMAIL: {
    graphqlTypename: "MarketingAutomationSendEmailStep",
    payloadKey: "sendEmail",
    insertable: true,
    requiresSchemaField: "Email",
  },
  SEND_SMS: {
    graphqlTypename: "MarketingAutomationSendSmsStep",
    payloadKey: "sendSms",
    insertable: true,
    requiresSchemaField: "Phone",
  },
  SEND_WEBHOOK: {
    graphqlTypename: "MarketingAutomationSendWebhookStep",
    payloadKey: "sendWebhook",
    insertable: true,
    requiresSchemaField: null,
  },
  MANAGE_CUSTOMER_TAGS: {
    graphqlTypename: "MarketingAutomationManageCustomerTagsStep",
    payloadKey: "manageCustomerTags",
    insertable: true,
    requiresSchemaField: null,
  },
  MANAGE_STATIC_SEGMENT: {
    graphqlTypename: "MarketingAutomationManageStaticSegmentStep",
    payloadKey: "manageStaticSegment",
    insertable: true,
    requiresSchemaField: null,
  },
  END: {
    graphqlTypename: "MarketingAutomationEndStep",
    payloadKey: "end",
    insertable: true,
    requiresSchemaField: null,
  },
  AB_TEST: {
    graphqlTypename: "MarketingAutomationAbTestStep",
    payloadKey: "abTest",
    insertable: true,
    requiresSchemaField: null,
  },
  CONDITION: {
    graphqlTypename: "MarketingAutomationConditionStep",
    payloadKey: "condition",
    insertable: true,
    requiresSchemaField: null,
  },
} as const;

export type PublicTypedAutomationStepGraphqlKind =
  keyof typeof TYPED_STEP_KIND_SPEC;

export type PublicTypedAutomationStepPayloadKey =
  (typeof TYPED_STEP_KIND_SPEC)[PublicTypedAutomationStepGraphqlKind]["payloadKey"];

export type InsertableAutomationStepPayloadKey = {
  [K in PublicTypedAutomationStepGraphqlKind]: (typeof TYPED_STEP_KIND_SPEC)[K]["insertable"] extends true
    ? (typeof TYPED_STEP_KIND_SPEC)[K]["payloadKey"]
    : never;
}[PublicTypedAutomationStepGraphqlKind];

export type PublicTypedAutomationStepGraphqlTypename =
  (typeof TYPED_STEP_KIND_SPEC)[PublicTypedAutomationStepGraphqlKind]["graphqlTypename"];

export type PublicTypedAutomationStepTreatment = {
  [K in PublicTypedAutomationStepGraphqlKind]: {
    readonly treatment: "typed";
    readonly graphqlKind: K;
    readonly graphqlTypename: (typeof TYPED_STEP_KIND_SPEC)[K]["graphqlTypename"];
    readonly payloadKey: (typeof TYPED_STEP_KIND_SPEC)[K]["payloadKey"];
    readonly insertable: (typeof TYPED_STEP_KIND_SPEC)[K]["insertable"];
  } & ((typeof TYPED_STEP_KIND_SPEC)[K] extends {
    readonly requiresSchemaField: infer F;
  }
    ? { readonly requiresSchemaField: F }
    : {});
}[PublicTypedAutomationStepGraphqlKind];

export interface PublicUnknownAutomationStepTreatment {
  readonly treatment: "unknown";
}

export type PublicAutomationStepTreatment =
  | PublicTypedAutomationStepTreatment
  | PublicUnknownAutomationStepTreatment;

const unknown = { treatment: "unknown" } as const;

export const MARKETING_AUTOMATION_PUBLIC_STEP = {
  [StepType.TRIGGER]: {
    treatment: "typed",
    graphqlKind: "TRIGGER",
    ...TYPED_STEP_KIND_SPEC.TRIGGER,
  },
  [StepType.WAIT]: {
    treatment: "typed",
    graphqlKind: "WAIT",
    ...TYPED_STEP_KIND_SPEC.WAIT,
  },
  [StepType.WAIT_FOR_EVENT]: {
    treatment: "typed",
    graphqlKind: "WAIT_FOR_EVENT",
    ...TYPED_STEP_KIND_SPEC.WAIT_FOR_EVENT,
  },
  [StepType.SEND_EMAIL]: {
    treatment: "typed",
    graphqlKind: "SEND_EMAIL",
    ...TYPED_STEP_KIND_SPEC.SEND_EMAIL,
  },
  [StepType.SEND_SMS]: {
    treatment: "typed",
    graphqlKind: "SEND_SMS",
    ...TYPED_STEP_KIND_SPEC.SEND_SMS,
  },
  [StepType.SEND_WEBHOOK]: {
    treatment: "typed",
    graphqlKind: "SEND_WEBHOOK",
    ...TYPED_STEP_KIND_SPEC.SEND_WEBHOOK,
  },
  [StepType.MANAGE_CUSTOMER_TAGS]: {
    treatment: "typed",
    graphqlKind: "MANAGE_CUSTOMER_TAGS",
    ...TYPED_STEP_KIND_SPEC.MANAGE_CUSTOMER_TAGS,
  },
  [StepType.MANAGE_STATIC_SEGMENT]: {
    treatment: "typed",
    graphqlKind: "MANAGE_STATIC_SEGMENT",
    ...TYPED_STEP_KIND_SPEC.MANAGE_STATIC_SEGMENT,
  },
  [StepType.DO_NOTHING]: {
    treatment: "typed",
    graphqlKind: "END",
    ...TYPED_STEP_KIND_SPEC.END,
  },
  [StepType.AB_TEST]: {
    treatment: "typed",
    graphqlKind: "AB_TEST",
    ...TYPED_STEP_KIND_SPEC.AB_TEST,
  },
  [StepType.CONDITION]: {
    treatment: "typed",
    graphqlKind: "CONDITION",
    ...TYPED_STEP_KIND_SPEC.CONDITION,
  },
  [StepType.SHOPPER_AGENT_MESSAGE]: unknown,
  [StepType.RECOVER]: unknown,
  [StepType.CONCIERGE_BLOCK]: unknown,
  [StepType.UPSELL]: unknown,
  [StepType.DOWNSELL]: unknown,
  [StepType.UPSELL_CONDITION]: unknown,
  [StepType.OMS_ACTION]: unknown,
  [StepType.OMS_CONDITION]: unknown,
  [StepType.OMS_CODE]: unknown,
  [StepType.RCO_SPLIT]: unknown,
  [StepType.CHECKOUT_EXPERIENCE]: unknown,
  [StepType.RCO_SEGMENT]: unknown,
  [StepType.BLOCK_CHECKOUT]: unknown,
  [StepType.RCO_CHECKOUT_PACKAGE]: unknown,
  [StepType.RCO_CHECKOUT_PACKAGE_CONDITION]: unknown,
  [StepType.POST_PURCHASE_PAGE]: unknown,
  [StepType.POST_PURCHASE_SEGMENT]: unknown,
  [StepType.POST_PURCHASE_SPLIT]: unknown,
  [StepType.TRACKING_PAGE]: unknown,
  [StepType.RULE_CONDITIONS]: unknown,
  [StepType.RULE_ACTIONS]: unknown,
  [StepType.RULE_CONDITIONS_V3]: unknown,
  [StepType.RULE_ACTIONS_V3]: unknown,
  [StepType.WORK_ORDER_WORK_STEP]: unknown,
  [StepType.WORK_ORDER_CONDITION]: unknown,
  [StepType.PROCESS_RETURN]: unknown,
  [StepType.COMPENSATION_STEP]: unknown,
  [StepType.RESTOCKING_STEP]: unknown,
  [StepType.RETURN_FLOW_CONDITION]: unknown,
  [StepType.HSA_QUESTION_GROUP]: unknown,
  [StepType.HSA_CONDITION]: unknown,
  [StepType.HSA_KNOCKOUT]: unknown,
  [StepType.HSA_CONTENT]: unknown,
  [StepType.HSA_END]: unknown,
  [StepType.SUPPLIES_SHIPPING_CONDITION]: unknown,
  [StepType.SUPPLIES_SHIPPING_RATE]: unknown,
} as const satisfies Record<StepType, PublicAutomationStepTreatment>;

export type UpdatableAutomationStepType = {
  [K in StepType]: (typeof MARKETING_AUTOMATION_PUBLIC_STEP)[K] extends {
    readonly treatment: "typed";
  }
    ? K
    : never;
}[StepType];

export type InsertableAutomationStepType = {
  [K in StepType]: (typeof MARKETING_AUTOMATION_PUBLIC_STEP)[K] extends {
    readonly treatment: "typed";
    readonly insertable: true;
  }
    ? K
    : never;
}[StepType];

export function isTypedPublicAutomationStepType(
  type: StepType,
): type is UpdatableAutomationStepType {
  return MARKETING_AUTOMATION_PUBLIC_STEP[type].treatment === "typed";
}

export function isInsertablePublicAutomationStepType(
  type: StepType,
): type is InsertableAutomationStepType {
  const spec = MARKETING_AUTOMATION_PUBLIC_STEP[type];
  return spec.treatment === "typed" && spec.insertable;
}

export function allStepTypes(): StepType[] {
  return Object.values(StepType) as StepType[];
}

export function typedPublicAutomationStepTypes(): UpdatableAutomationStepType[] {
  return allStepTypes().filter(isTypedPublicAutomationStepType);
}

export function insertablePublicAutomationStepTypes(): InsertableAutomationStepType[] {
  return allStepTypes().filter(isInsertablePublicAutomationStepType);
}

export function unknownPublicAutomationStepTypes(): StepType[] {
  return allStepTypes().filter(
    (type) => MARKETING_AUTOMATION_PUBLIC_STEP[type].treatment === "unknown",
  );
}

export function typedPublicAutomationStepPayloads(): [
  PublicTypedAutomationStepPayloadKey,
  UpdatableAutomationStepType,
][] {
  return typedPublicAutomationStepTypes().map((type) => {
    const spec = MARKETING_AUTOMATION_PUBLIC_STEP[type];
    if (spec.treatment !== "typed") {
      throw new Error(`Expected typed treatment for ${type}`);
    }
    return [spec.payloadKey, type];
  });
}

export function insertablePublicAutomationStepPayloads(): [
  InsertableAutomationStepPayloadKey,
  InsertableAutomationStepType,
][] {
  return insertablePublicAutomationStepTypes().map((type) => {
    const spec = MARKETING_AUTOMATION_PUBLIC_STEP[type];
    if (spec.treatment !== "typed" || !spec.insertable) {
      throw new Error(`Expected insertable treatment for ${type}`);
    }
    return [spec.payloadKey, type];
  });
}

export function classifyStoredAutomationStepType(
  storedType: string,
): PublicAutomationStepTreatment {
  for (const type of allStepTypes()) {
    if (type === storedType) {
      return MARKETING_AUTOMATION_PUBLIC_STEP[type];
    }
  }
  return { treatment: "unknown" };
}

function schemaTypeOf(schemaType: string): SchemaType | undefined {
  for (const value of Object.values(SchemaType)) {
    if (value === schemaType) {
      return value;
    }
  }
  return undefined;
}

export function parsePublicAutomationSchemaType(
  schemaType: string,
): SchemaType | undefined {
  return schemaTypeOf(schemaType);
}

export function schemaHasDataType(
  schemaType: string,
  dataType: AutomationStepRequiredSchemaField,
): boolean {
  const typed = schemaTypeOf(schemaType);
  if (typed == null) {
    return false;
  }
  return Object.values(schemas[typed].fields).some(
    (field) =>
      field != null && removeGenericFromType(field.dataType) === dataType,
  );
}

export function isInsertablePublicAutomationStepForSchema(
  type: StepType,
  schemaType: string,
): type is InsertableAutomationStepType {
  const spec = MARKETING_AUTOMATION_PUBLIC_STEP[type];
  if (spec.treatment !== "typed" || !spec.insertable) {
    return false;
  }
  if (
    spec.requiresSchemaField != null &&
    !schemaHasDataType(schemaType, spec.requiresSchemaField)
  ) {
    return false;
  }
  return true;
}

export function insertablePublicAutomationStepTypesForSchema(
  schemaType: string,
): InsertableAutomationStepType[] {
  return insertablePublicAutomationStepTypes().filter((type) =>
    isInsertablePublicAutomationStepForSchema(type, schemaType),
  );
}
